import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { App, Button, Popconfirm, Progress, Tag, Tooltip } from "antd";
import { DownloadOutlined, ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import GenericCard from "components/UI/GenericCard/GenericCard";
import AntTable from "components/UI/AntTable";
import DashedTooltip from "components/UI/DashedTooltip";
import CloseOptionTradeModal from "./CloseOptionTradeModal";
import {
  apiGetOptionTrades,
  apiTriggerOptionSell,
} from "api/apiOptionStrategies";
import OptionTradeBreakdown from "./OptionTradeBreakdown";
import { formatMoney } from "./optionTradeUtils";
import {
  formatDay,
  formatIv,
  formatMarketDateTime,
  formatMarketDateTimeText,
  formatPercent,
  formatPoints,
  formatPrice,
} from "./optionStrategyUtils";
import { returnUpDownClassname } from "utils/helpers";
import { useTradingRefresh } from "components/Trading/tradingRefresh";
import StrategyLink from "components/Trading/StrategyLink";
import PnlSummaryLine from "components/Strategies/PnlSummaryLine";
import { downloadCsv } from "utils/csv";

// same as OPEN_POSITION_STATUSES on the backend
const OPEN_STATUSES = [
  "active",
  "pending-close",
  "close-in-progress",
  "automated-close-failed",
];

const contractLabel = (trade) =>
  `${trade.optionTrade?.strikePrice} ${trade.optionTrade?.optionType || "call"}`;

const money = (value) => (
  <span className={returnUpDownClassname(value)}>${formatMoney(value)}</span>
);

const percent = (value) => (
  <span className={returnUpDownClassname(value)}>{formatPercent(value)}</span>
);

const points = (value) => (
  <span className={returnUpDownClassname(value)}>{formatPoints(value)}</span>
);

const FillTime = ({ value }) => {
  const { day, time } = formatMarketDateTime(value);
  return (
    <div>
      {day}
      {time && <span className="ml-1 text-xs opacity-60">{time}</span>}
    </div>
  );
};

FillTime.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
};

// computed by the backend for closed trades (closedTradeBreakdown)
const NO_BREAKDOWN = {
  underlying: {},
  iv: {},
  model: {},
  fills: {},
  timing: {},
};
const breakdownOf = (trade) => trade.breakdown || NO_BREAKDOWN;

// computed by the backend for open trades (openTradeStatus)
const NO_POSITION = { underlying: {}, iv: {}, range: {} };
const positionOf = (trade) => trade.position || NO_POSITION;

const EXPIRY_WARNING_DAYS = 2;

const moneynessLabel = (value) =>
  Math.abs(value) < 0.5 ? "ATM" : value > 0 ? "ITM" : "OTM";

const byNumber = (get) => (a, b) => (get(a) || 0) - (get(b) || 0);

const sumOf = (rows, get) =>
  rows.reduce((acc, row) => acc + (get(row) || 0), 0);

const cents = (value) => (value == null ? "" : Math.round(value * 100) / 100);

const CSV_COLUMNS = [
  { title: "Symbol", value: (t) => t.symbol },
  { title: "Strategy", value: (t) => t.strategy?.name || "Deleted strategy" },
  { title: "Option type", value: (t) => t.optionTrade?.optionType },
  { title: "Strike", value: (t) => t.optionTrade?.strikePrice },
  {
    title: "Expiration",
    value: (t) => formatDay(t.optionTrade?.expirationDate),
  },
  { title: "Opened (ET)", value: (t) => formatMarketDateTimeText(t.openDate) },
  { title: "Closed (ET)", value: (t) => formatMarketDateTimeText(t.closeDate) },
  { title: "Bars held", value: (t) => t.dealDaysOpen },
  { title: "Contracts", value: (t) => t.amount },
  { title: "Entry premium", value: (t) => t.openRate },
  { title: "Exit premium", value: (t) => t.closeRate },
  { title: "Fees", value: (t) => cents(t.premiumFee) },
  { title: "Net profit", value: (t) => cents(t.netProfit?.value) },
  { title: "Net profit %", value: (t) => t.netProfit?.perc },
  {
    title: "Underlying at open",
    value: (t) => breakdownOf(t).underlying.open,
  },
  {
    title: "Underlying at exit",
    value: (t) => breakdownOf(t).underlying.close,
  },
  {
    title: "Underlying move %",
    value: (t) => cents(breakdownOf(t).underlying.movePercent),
  },
  { title: "Target", value: (t) => t.optionTrade?.targetUnderlyingPrice },
  { title: "IV at open", value: (t) => t.optionTrade?.ivAtOpen },
  { title: "IV at exit", value: (t) => t.optionTrade?.ivAtClose },
  {
    title: "Predicted best %",
    value: (t) => t.optionTrade?.predictedBestProfitPercent,
  },
  {
    title: "Predicted worst %",
    value: (t) => t.optionTrade?.predictedWorstProfitPercent,
  },
  {
    title: "Model exit premium (open IV)",
    value: (t) => cents(breakdownOf(t).model.openIvPrice),
  },
  {
    title: "Model profit % (open IV)",
    value: (t) => cents(breakdownOf(t).model.openIvPercent),
  },
  {
    title: "Model profit % (exit IV)",
    value: (t) => cents(breakdownOf(t).model.exitIvPercent),
  },
  {
    title: "Gap to model pts",
    value: (t) => cents(breakdownOf(t).model.gapPoints),
  },
  {
    title: "IV effect pts",
    value: (t) => cents(breakdownOf(t).model.ivEffectPoints),
  },
  {
    title: "Execution pts",
    value: (t) => cents(breakdownOf(t).model.executionPoints),
  },
  {
    title: "Entry vs mid %",
    value: (t) => cents(breakdownOf(t).fills.entryVsMidPercent),
  },
  {
    title: "Exit vs mid %",
    value: (t) => cents(breakdownOf(t).fills.exitVsMidPercent),
  },
  { title: "Reason", value: (t) => t.closeSignal || "manual" },
];

const strategyColumns = [
  {
    title: "Symbol",
    dataIndex: "symbol",
    sorter: { compare: (a, b) => a.symbol.localeCompare(b.symbol) },
  },
  {
    title: "Strategy",
    key: "strategy",
    render: (t) => (
      <StrategyLink
        strategyId={t.strategy?._id}
        name={t.strategy?.name}
        instrument="options"
      />
    ),
  },
  { title: "Contract", key: "contract", render: contractLabel },
];

// Option positions or history across all option strategies (the Options view
// of the Trading page), for the account type picked in the header.
const OptionTradesTable = ({ view }) => {
  const { message } = App.useApp();
  const accountType = useSelector((state) => state.main.accountType);
  const { refreshKey, notifyChanged } = useTradingRefresh();
  const [trades, setTrades] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [closing, setClosing] = useState(null);

  const load = async () => {
    setIsLoading(true);
    try {
      setTrades(await apiGetOptionTrades());
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to load trades");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const triggerSell = async (trade) => {
    try {
      await apiTriggerOptionSell(trade._id);
      message.success("Sell triggered, AutoTrader will sell it");
      notifyChanged();
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Failed to trigger the sell",
      );
    }
  };

  const rows = useMemo(() => {
    const ofAccount = trades.filter((t) => t.accountType === accountType);
    if (view === "open") {
      return ofAccount.filter((t) => OPEN_STATUSES.includes(t.orderStatus));
    }
    return ofAccount
      .filter((t) => t.orderStatus === "closed")
      .sort((a, b) => new Date(b.closeDate) - new Date(a.closeDate));
  }, [trades, accountType, view]);

  const openColumns = [
    ...strategyColumns,
    {
      title: (
        <DashedTooltip text="Calendar days left to expiration">
          Expiration
        </DashedTooltip>
      ),
      key: "expiration",
      render: (t) => {
        const { dte } = positionOf(t);
        return (
          <div>
            {formatDay(t.optionTrade?.expirationDate)}
            {dte != null && (
              <div
                className={`text-xs ${dte <= EXPIRY_WARNING_DAYS ? "font-semibold text-red-500" : "opacity-60"}`}
              >
                {dte === 0 ? "expires today" : `${dte}d left`}
              </div>
            )}
          </div>
        );
      },
      sorter: {
        compare: (a, b) =>
          new Date(a.optionTrade?.expirationDate) -
          new Date(b.optionTrade?.expirationDate),
      },
    },
    {
      title: (
        <DashedTooltip text="Underlying's last price (AutoTrader live, else the 15:50 run) and its change since the entry. Below: distance from the strike (+ = in the money)">
          Underlying
        </DashedTooltip>
      ),
      key: "underlying",
      render: (t) => {
        const { underlying } = positionOf(t);
        if (underlying.last == null) return "-";
        return (
          <div>
            {formatPrice(underlying.last)} ({percent(underlying.movePercent)})
            <div className="text-xs opacity-60">
              from {formatPrice(underlying.open)}
            </div>
          </div>
        );
      },
      sorter: {
        compare: byNumber((t) => positionOf(t).underlying.movePercent),
      },
    },
    {
      title: (
        <DashedTooltip text="Take-profit target on the underlying (set at the buy signal): the share of the way from the entry covered so far, and the move still needed">
          Target
        </DashedTooltip>
      ),
      key: "target",
      render: (t) => {
        const { underlying } = positionOf(t);
        if (underlying.target == null) return "-";
        const reached = underlying.toTargetPercent <= 0;
        return (
          <div className="min-w-[110px]">
            {formatPrice(underlying.target)}
            {/* {underlying.targetProgress != null && (
              <Progress
                percent={Math.max(0, underlying.targetProgress)}
                size="small"
                showInfo={false}
                status={reached ? "success" : "normal"}
              />
            )} */}
            {underlying.toTargetPercent != null && (
              <div className="text-xs opacity-60">
                {reached
                  ? "reached ✓"
                  : `${formatPercent(underlying.toTargetPercent)} to go`}
              </div>
            )}
          </div>
        );
      },
      sorter: {
        compare: byNumber((t) => positionOf(t).underlying.toTargetPercent),
      },
    },
    {
      title: "Contracts",
      key: "contracts",
      render: (t) => (
        <div>
          {t.amount}
          <div className="text-xs opacity-60">
            cost ${formatMoney(t.openValue)}
          </div>
        </div>
      ),
      sorter: { compare: byNumber((t) => t.openValue) },
    },
    {
      title: (
        <DashedTooltip text="Premium per share: entry fill → current bid/ask mid (AutoTrader live during the contract's hours, else the 15:50 run)">
          Entry → Current
        </DashedTooltip>
      ),
      key: "rates",
      render: (t) => {
        const { markedAt } = positionOf(t);
        return (
          <div>
            {formatPrice(t.openRate)} → {formatPrice(t.currentRate)}
            {markedAt && (
              <div className="text-xs opacity-60">
                {formatMarketDateTimeText(markedAt)}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: (
        <DashedTooltip text="Unrealized P&L at the current premium (before exit fees)">
          P&L
        </DashedTooltip>
      ),
      key: "pnl",
      width: 190,
      render: (t) =>
        t.currentRate != null ? (
          <div className="whitespace-nowrap">
            {money(t.profit?.value)} ({percent(t.profit?.perc)})
          </div>
        ) : (
          "-"
        ),
      sorter: { compare: byNumber((t) => t.profit?.value) },
    },
    {
      title: (
        <DashedTooltip text="Premium P&L % at the lowest mark so far (15:50 runs and AutoTrader live)">
          Min
        </DashedTooltip>
      ),
      key: "min",
      width: 100,
      render: (t) => percent(positionOf(t).range.worstPercent),
      sorter: { compare: byNumber((t) => positionOf(t).range.worstPercent) },
    },
    {
      title: (
        <DashedTooltip text="Premium P&L % at the highest mark so far (15:50 runs and AutoTrader live)">
          Max
        </DashedTooltip>
      ),
      key: "max",
      width: 100,
      render: (t) => percent(positionOf(t).range.bestPercent),
      sorter: { compare: byNumber((t) => positionOf(t).range.bestPercent) },
    },
    {
      title: (
        <DashedTooltip text="Contract IV at the entry → now (IB's model IV from AutoTrader live, else the 15:50 run)">
          IV
        </DashedTooltip>
      ),
      key: "iv",
      render: (t) => {
        const { iv } = positionOf(t);
        return `${formatIv(iv.open)} → ${formatIv(iv.last)}`;
      },
    },
    { title: "Bars open", dataIndex: "dealDaysOpen" },
    {
      title: "Status",
      key: "status",
      render: (t) =>
        t.orderStatus === "pending-close" ? (
          <Tooltip title={t.closeReason}>
            <Tag color="gold">Exit advised</Tag>
          </Tooltip>
        ) : t.orderStatus === "close-in-progress" ? (
          <Tag color="processing">Closing</Tag>
        ) : t.orderStatus === "automated-close-failed" ? (
          <Tooltip title="AutoTrader couldn't close this position; close it yourself">
            <Tag color="red">Close failed</Tag>
          </Tooltip>
        ) : (
          <Tag color="blue">Open</Tag>
        ),
    },
    {
      title: "Trigger Sell",
      key: "triggerSell",
      render: (trade) =>
        trade.autoTraded && trade.orderStatus === "active" ? (
          <Popconfirm
            title={`Sell ${trade.amount} ${trade.symbol} ${contractLabel(trade)}?`}
            description="AutoTrader sells it (urgent) in the contract's trading hours"
            okText="Trigger sell"
            onConfirm={() => triggerSell(trade)}
          >
            <Button size="small" type="primary">
              Trigger
            </Button>
          </Popconfirm>
        ) : null,
    },
    {
      title: "",
      key: "actions",
      render: (trade) => (
        <Button size="small" onClick={() => setClosing(trade)}>
          Close
        </Button>
      ),
    },
  ];

  const closedColumns = [
    ...strategyColumns,
    {
      title: (
        <DashedTooltip text="Fill times in New York. Manually logged fills have only a date">
          Opened → Closed
        </DashedTooltip>
      ),
      key: "dates",
      render: (t) => (
        <div className="leading-tight">
          <FillTime value={t.openDate} />
          <FillTime value={t.closeDate} />
        </div>
      ),
      sorter: {
        compare: (a, b) => new Date(a.closeDate) - new Date(b.closeDate),
      },
    },
    {
      title: (
        <DashedTooltip text="Trading bars held / calendar days to expiration at entry">
          Held
        </DashedTooltip>
      ),
      key: "held",
      render: (t) => {
        const { timing } = breakdownOf(t);
        return `${timing.bars ?? "-"} bars / ${timing.dteAtEntry ?? "-"} DTE`;
      },
      sorter: { compare: byNumber((t) => t.dealDaysOpen) },
    },
    {
      title: (
        <DashedTooltip text="Underlying from the open to the exit, against the take-profit target set at the buy signal">
          Underlying move
        </DashedTooltip>
      ),
      key: "underlying",
      render: (t) => {
        const { underlying } = breakdownOf(t);
        return (
          <>
            {percent(underlying.movePercent)}
            <div className="text-xs opacity-60">
              target {formatPercent(underlying.targetPercent)}
              {underlying.targetHit && " ✓"}
            </div>
          </>
        );
      },
      sorter: {
        compare: byNumber((t) => breakdownOf(t).underlying.movePercent),
      },
    },
    {
      title: "Entry → Exit",
      key: "rates",
      render: (t) => (
        <>
          {formatPrice(t.openRate)} → {formatPrice(t.closeRate)}
          <div className="text-xs opacity-60">x{t.amount}</div>
        </>
      ),
    },
    {
      title: "Net P&L",
      key: "net",
      render: (t) => (
        <>
          {money(t.netProfit?.value)} ({percent(t.netProfit?.perc)})
        </>
      ),
      sorter: { compare: byNumber((t) => t.netProfit?.value) },
    },
    {
      title: (
        <DashedTooltip text="Predicted at the buy signal: target hit next day / only at expiration">
          Predicted
        </DashedTooltip>
      ),
      key: "predicted",
      render: (t) => (
        <>
          {percent(t.optionTrade?.predictedBestProfitPercent)} /{" "}
          {percent(t.optionTrade?.predictedWorstProfitPercent)}
        </>
      ),
    },
    {
      title: (
        <DashedTooltip text="Model check: the opening IV priced with the actual underlying at exit. Expand the row for the breakdown">
          Model at exit
        </DashedTooltip>
      ),
      key: "model",
      render: (t) => {
        const { model } = breakdownOf(t);
        return model.openIvPrice != null ? (
          <>
            {formatPrice(model.openIvPrice)} ({percent(model.openIvPercent)})
          </>
        ) : (
          "-"
        );
      },
    },
    {
      title: (
        <DashedTooltip text="Actual gross % - model at open IV %, split into the IV change and execution (spread, fill vs quote)">
          Gap to model
        </DashedTooltip>
      ),
      key: "gap",
      render: (t) => {
        const { model } = breakdownOf(t);
        if (model.gapPoints == null) return "-";
        return (
          <>
            {points(model.gapPoints)}
            {model.ivEffectPoints != null && (
              <div className="text-xs opacity-60">
                IV {formatPoints(model.ivEffectPoints)} · exec{" "}
                {formatPoints(model.executionPoints)}
              </div>
            )}
          </>
        );
      },
      sorter: { compare: byNumber((t) => breakdownOf(t).model.gapPoints) },
    },
    {
      title: "IV open → exit",
      key: "iv",
      render: (t) =>
        `${formatIv(t.optionTrade?.ivAtOpen)} → ${formatIv(t.optionTrade?.ivAtClose)}`,
    },
    {
      title: "Reason",
      key: "reason",
      render: (t) => (
        <Tooltip title={t.closeReason}>
          <Tag>{t.closeSignal || "manual"}</Tag>
        </Tooltip>
      ),
    },
  ];

  const openCost = sumOf(rows, (t) => t.openValue);

  const title =
    view === "open"
      ? `Open option positions (${rows.length})`
      : `Option trades history (${rows.length} trades)`;

  return (
    <div className="cards-wrapper">
      <GenericCard title={title} className="table-card" maxHeight={610}>
        <div className="flex flex-wrap items-start gap-3">
          <div className="flex-1">
            <PnlSummaryLine
              items={[
                view === "open"
                  ? {
                      label: "Unrealized P&L",
                      tooltip:
                        "At the contracts' current premiums, % of the total cost",
                      value: sumOf(rows, (t) => t.profit?.value),
                      percent: openCost
                        ? Math.round(
                            (sumOf(rows, (t) => t.profit?.value) / openCost) *
                              1000,
                          ) / 10
                        : undefined,
                    }
                  : {
                      label: "Realized P&L",
                      tooltip: "Total net P&L of these trades",
                      value: sumOf(rows, (t) => t.netProfit?.value),
                    },
              ]}
            />
          </div>
          {view === "closed" && (
            <Button
              size="small"
              icon={<DownloadOutlined />}
              disabled={!rows.length}
              onClick={() =>
                downloadCsv(
                  `option-trades-history-${dayjs().format("YYYY-MM-DD")}.csv`,
                  rows,
                  CSV_COLUMNS,
                )
              }
            >
              Export CSV
            </Button>
          )}
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={notifyChanged}
            loading={isLoading}
          >
            Refresh
          </Button>
        </div>
        <AntTable
          rowKey="_id"
          columns={view === "open" ? openColumns : closedColumns}
          dataSource={rows}
          loading={isLoading}
          {...(view === "closed" && {
            // expanded rows have their own height, which the virtual list can't size
            virtual: false,
            expandable: {
              expandedRowRender: (trade) => (
                <OptionTradeBreakdown trade={trade} />
              ),
            },
          })}
        />
      </GenericCard>

      {closing && (
        <CloseOptionTradeModal
          trade={closing}
          onClose={() => setClosing(null)}
          onSaved={() => {
            setClosing(null);
            notifyChanged();
          }}
        />
      )}
    </div>
  );
};

OptionTradesTable.propTypes = {
  view: PropTypes.oneOf(["open", "closed"]).isRequired,
};

export default OptionTradesTable;
